import React, { useState } from 'react';
import { ocrConfigService } from '../../services';
import { OCRConfig, CreateOCRConfigRequest, Domain } from '../../types';
import { Modal } from '../ui';

interface Props {
  config: OCRConfig | null;
  duplicateFrom?: OCRConfig | null;
  domains: Domain[];
  onClose: () => void;
  onSave: () => void;
}

const PSM_OPTIONS = [
  { value: 0, label: '0 - OSD seulement', hint: 'Orientation et script' },
  { value: 1, label: '1 - Segmentation auto + OSD', hint: '' },
  { value: 2, label: '2 - Segmentation auto', hint: 'Sans OSD' },
  { value: 3, label: '3 - Auto complet', hint: 'Recommande' },
  { value: 4, label: '4 - Colonne unique', hint: 'Texte variable' },
  { value: 5, label: '5 - Bloc vertical', hint: '' },
  { value: 6, label: '6 - Bloc uniforme', hint: 'Actes notaries' },
  { value: 7, label: '7 - Ligne unique', hint: '' },
  { value: 8, label: '8 - Mot unique', hint: '' },
  { value: 9, label: '9 - Mot dans cercle', hint: '' },
  { value: 10, label: '10 - Caractere unique', hint: '' },
  { value: 11, label: '11 - Texte sparse', hint: '' },
  { value: 12, label: '12 - Sparse + OSD', hint: '' },
  { value: 13, label: '13 - Ligne brute', hint: '' },
];

const OEM_OPTIONS = [
  { value: 0, label: '0 - Legacy Tesseract', hint: '' },
  { value: 1, label: '1 - LSTM seulement', hint: '' },
  { value: 2, label: '2 - Legacy + LSTM', hint: '' },
  { value: 3, label: '3 - Auto', hint: 'Recommande' },
];

const DPI_OPTIONS = [
  { value: 150, label: '150 DPI', hint: 'Rapide, qualite reduite' },
  { value: 200, label: '200 DPI', hint: 'Standard' },
  { value: 300, label: '300 DPI', hint: 'Haute qualite' },
  { value: 400, label: '400 DPI', hint: 'Tres haute qualite, lent' },
];

const LANGUAGE_OPTIONS = [
  { value: 'fra', label: 'Francais' },
  { value: 'eng', label: 'Anglais' },
  { value: 'fra+eng', label: 'Francais + Anglais' },
];

const PAGE_SEPARATOR_OPTIONS = [
  { value: 'none', label: 'Aucun', hint: 'Pages séparées par double saut de ligne' },
  { value: 'simple', label: 'Simple', hint: 'Double saut de ligne entre pages' },
  { value: 'marker', label: 'Marqueurs', hint: '--- Page X --- entre chaque page' },
];

export const OcrConfigModal: React.FC<Props> = ({ config, duplicateFrom, domains, onClose, onSave }) => {
  const isEdit = !!config;
  const isDuplicate = !!duplicateFrom;
  const source = config || duplicateFrom;
  const [formData, setFormData] = useState({
    domain: source?.domain || (domains[0]?.name || ''),
    name: isDuplicate ? `${source?.name} (copie)` : (source?.name || ''),
    version: isDuplicate ? '' : (source?.version || ''),
    description: source?.description || '',
    dpi: source?.dpi || 200,
    psm: source?.psm || 3,
    oem: source?.oem || 3,
    language: source?.language || 'fra',
    page_separator_format: source?.page_separator_format || 'none',
    min_text_threshold: source?.min_text_threshold || 100,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEdit && config) {
        await ocrConfigService.update(config.id, {
          name: formData.name,
          description: formData.description || undefined,
          dpi: formData.dpi,
          psm: formData.psm,
          oem: formData.oem,
          language: formData.language,
          page_separator_format: formData.page_separator_format as 'none' | 'simple' | 'marker',
          min_text_threshold: formData.min_text_threshold,
        });
      } else {
        await ocrConfigService.create({
          domain: formData.domain,
          name: formData.name,
          version: formData.version,
          description: formData.description || undefined,
          dpi: formData.dpi,
          psm: formData.psm,
          oem: formData.oem,
          language: formData.language,
          page_separator_format: formData.page_separator_format as 'none' | 'simple' | 'marker',
          min_text_threshold: formData.min_text_threshold,
        } as CreateOCRConfigRequest);
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Modifier la Configuration OCR' : (isDuplicate ? 'Dupliquer la Configuration OCR' : 'Nouvelle Configuration OCR')}
      size="lg"
    >
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Domaine <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              disabled={isEdit}
              className="w-full border rounded px-3 py-2"
              required
            >
              {domains.map((d, index) => (
                <option key={`${d.name}-${index}`} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Le domaine ne peut pas etre modifie apres creation
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Version <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              disabled={isEdit}
              className="w-full border rounded px-3 py-2"
              placeholder="ex: 1.0.0"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              La version ne peut pas etre modifiee apres creation
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full border rounded px-3 py-2"
            placeholder="ex: Config haute precision"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full border rounded px-3 py-2"
            rows={2}
            placeholder="Description optionnelle de cette configuration"
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">DPI (Resolution)</label>
            <select
              value={formData.dpi}
              onChange={(e) => setFormData({ ...formData, dpi: parseInt(e.target.value) })}
              className="w-full border rounded px-3 py-2"
            >
              {DPI_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.hint && `- ${option.hint}`}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Resolution de conversion PDF vers image
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Langue</label>
            <select
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Langue du modele Tesseract
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">PSM (Page Segmentation Mode)</label>
            <select
              value={formData.psm}
              onChange={(e) => setFormData({ ...formData, psm: parseInt(e.target.value) })}
              className="w-full border rounded px-3 py-2"
            >
              {PSM_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.hint && `- ${option.hint}`}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Mode de segmentation de page Tesseract
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">OEM (OCR Engine Mode)</label>
            <select
              value={formData.oem}
              onChange={(e) => setFormData({ ...formData, oem: parseInt(e.target.value) })}
              className="w-full border rounded px-3 py-2"
            >
              {OEM_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.hint && `- ${option.hint}`}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Moteur OCR Tesseract a utiliser
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Format de séparation des pages</label>
          <select
            value={formData.page_separator_format}
            onChange={(e) => setFormData({ ...formData, page_separator_format: e.target.value as 'none' | 'simple' | 'marker' })}
            className="w-full border rounded px-3 py-2"
          >
            {PAGE_SEPARATOR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} {option.hint && `- ${option.hint}`}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Définit comment le texte des différentes pages est combiné avant l'envoi au LLM
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Seuil minimum de texte</label>
          <input
            type="number"
            value={formData.min_text_threshold}
            onChange={(e) => setFormData({ ...formData, min_text_threshold: parseInt(e.target.value) || 100 })}
            min="0"
            max="10000"
            step="10"
            className="w-full border rounded px-3 py-2"
          />
          <p className="text-xs text-gray-500 mt-1">
            Nombre minimum de caractères non-blancs pour considérer le texte PDF natif comme valide.
            En dessous de ce seuil, l'OCR sera utilisé à la place.
          </p>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
